///
/// EarthBrowser
///
/// Created by Matt Giger on 9/12/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Capturing the current camera pose as a saved view: auto-name from the
/// local locations DB, store in the savedViews slice, queue a thumbnail
/// capture and show the rename toast.
///

import { CameraParams, EarthCameraState } from "@/earthbrowser/activities/EarthActivity/EarthCameraState"
import { LocationsTable } from "@/earthbrowser/dataset-distribution/locations/LocationsTable"
import { ViewThumbnailStore } from "@/earthbrowser/camera-director/ViewThumbnailStore"
import { geographic, wgs } from "@/earthbrowser/utils/geographic"
import { spherical } from "@/earthbrowser/utils/spherical"
import type { CameraSavedState } from "@/types/CameraSavedState"
import { setSavedViewToast } from "@/redux/dialogSlice"
import { upsertView, type SavedView } from "@/redux/savedViewsSlice"
import { appStore } from "@/redux/store"
import { parseTarget } from "@/utils/navigationHistory"
import { setCameraPoseParams } from "@/utils/cameraPoseUrl"
import { trig } from "@stovokor/math/math"

/** Progressive search radii (degrees) for the wide-view place lookup */
const cLookupRadiiDeg = [4, 12]
const cLookupLimit = 30

/**
 * Below this camera altitude a view is "close": it frames a single town,
 * so it's named for the literally nearest place. Above it, the view spans
 * a region and gets the most prominent nearby city instead. (With a ~60°
 * FOV the ground span is roughly the altitude, so 300 km ≈ a state-sized
 * view.)
 */
const cCloseAltitudeKm = 300
const cNearestRadiusDeg = 1

/**
 * Save the current camera pose as a named view. A focused entity names
 * the view after itself ("ISS (ZARYA)"); otherwise the name comes from a
 * reverse lookup against the local locations DB ("View over Reykjavík"),
 * falling back to formatted coordinates over open ocean. The save toast
 * opens with the name pre-selected for inline rename.
 */
export async function saveCurrentView(): Promise<void> {
	const state = appStore.getState()
	const camera = state.earthbrowser.cameraState
	const now = new Date().toISOString()

	// The ?target= URL param is the canonical "focused entity" signal
	// (pushed on focus, cleared on release), so a view saved while an
	// entity is focused can refocus it on fly-to.
	const focusTarget = new URL(window.location.href).searchParams.get("target")

	// A focused target names the view after itself (the info window title
	// is its display name: "ISS (ZARYA)", "Kīlauea", ...). The
	// nearest-place lookup only applies to free camera views - a satellite
	// overhead has nothing to do with the city below it.
	const infoWindow = state.userInterface.infoWindow
	const focusName = focusTarget && infoWindow.isOpen ? infoWindow.title.trim() : ""

	// Visible layers are recorded as enable-only dependencies: flying to
	// the view turns missing ones on, never anything off.
	const visibleDatasets = state.earthbrowser.visibleDatasets

	const view: SavedView = {
		id: crypto.randomUUID(),
		name: focusName.length > 0 ? focusName : `View over ${await autoNameForCamera(camera)}`,
		camera: { ...camera },
		focusTargetId: focusTarget ?? undefined,
		layers: visibleDatasets.length > 0 ? [...visibleDatasets] : undefined,
		tags: [],
		createdAt: now,
		updatedAt: now,
	}

	// Thumbnail is captured by the engine's post-render hook on the next frame
	ViewThumbnailStore.shared.requestCapture(view.id)

	appStore.dispatch(upsertView(view))
	appStore.dispatch(setSavedViewToast(view.id))
}

/**
 * Reverse lookup of a place name at the fulcrum - the point the camera is
 * looking at, not the point below it (with a tilted view those differ by
 * many kilometers, and the view should be named for what's on screen).
 * Returns the bare place name ("Reykjavík"), or formatted coordinates
 * over open ocean; callers add their own framing ("View over ...").
 *
 * Close views use the distance-ordered queryNearest: a low camera over
 * Pacific City must name it, not the bigger Lincoln City down the coast.
 * Wide views keep the rank-ordered queryByBBox candidates + haversine
 * pick, where the limit truncation is a feature - a regional view should
 * be "View over Portland", not the nearest hamlet to the fulcrum.
 */
export async function autoNameForCamera(camera: CameraSavedState): Promise<string> {
	const fulcrum = EarthCameraState.fulcrumForParams(
		new CameraParams(camera.radius, camera.theta, camera.phi, camera.tilt, camera.bearing)
	)
	const geo = new geographic(fulcrum)
	const lat = geo.latitude * trig.deg
	const lon = geo.longitude * trig.deg

	try {
			if (camera.radius - wgs.a < cCloseAltitudeKm) {
			const rows = await LocationsTable.queryNearest(lat, lon, cNearestRadiusDeg, 1)
			if (rows.length > 0) {
				return rows[0].name
			}
			// Nothing within the tight radius (coastal camera looking out
			// to sea, remote areas): fall through to the wide lookup
		}

		for (const radiusDeg of cLookupRadiiDeg) {
			const rows = await LocationsTable.queryByBBox(
				lon - radiusDeg, lat - radiusDeg,
				lon + radiusDeg, lat + radiusDeg,
				cLookupLimit
			)
			if (rows.length === 0) continue

			let best = rows[0]
			let bestDist = haversineKm(lat, lon, best.lat, best.lon)
			for (const row of rows) {
				const dist = haversineKm(lat, lon, row.lat, row.lon)
				if (dist < bestDist) {
					best = row
					bestDist = dist
				}
			}
			return best.name
		}
	} catch (error) {
		console.warn("[savedViews] Nearest-place lookup failed:", error)
	}

	// Open ocean / DB unavailable: fall back to formatted coordinates
	const latLabel = `${Math.abs(lat).toFixed(1)}°${lat >= 0 ? "N" : "S"}`
	const lonLabel = `${Math.abs(lon).toFixed(1)}°${lon >= 0 ? "E" : "W"}`
	return `${latLabel}, ${lonLabel}`
}

/**
 * Self-contained share URL for a saved view. The recipient needs no
 * account or saved-view record: ?lat/?lon/?alt (degrees, km) restore the
 * exact pose in a human-readable form, ?layers= enables the view's layer
 * dependencies (enable-only), and ?target= refocuses the entity (which
 * wins over the pose when present, since the focus transition frames the
 * entity itself).
 */
export function viewShareUrl(view: Pick<SavedView, "camera" | "layers" | "focusTargetId">): string {
	const url = new URL(window.location.origin)
	setCameraPoseParams(url, view.camera)
	if (view.layers && view.layers.length > 0) {
		url.searchParams.set("layers", view.layers.join(","))
	}
	if (view.focusTargetId) {
		url.searchParams.set("target", view.focusTargetId)
	}
	return url.toString()
}

/**
 * Snapshot of the live view in SavedView terms (camera, enable-only
 * layers, focused target): what Share and Save both capture.
 */
export function currentViewSnapshot(): Pick<SavedView, "camera" | "layers" | "focusTargetId"> {
	const state = appStore.getState().earthbrowser
	const target = new URL(window.location.href).searchParams.get("target")
	return {
		camera: { ...state.cameraState },
		layers: [...state.visibleDatasets],
		focusTargetId: target ?? undefined,
	}
}

/**
 * Share URL for the currently focused feature (the live ?target= param).
 * ?layers= carries just the entity's own dataset layer (enable-only) so
 * the target exists for the recipient; ?target= then focuses and frames
 * it on launch. Returns null when nothing is focused.
 */
export function featureShareUrl(): string | null {
	const target = new URL(window.location.href).searchParams.get("target")
	if (!target) return null

	const url = new URL(window.location.origin)
	const parsed = parseTarget(target)
	if (parsed?.kind === "entity") {
		url.searchParams.set("layers", parsed.type)
	}
	url.searchParams.set("target", target)
	return url.toString()
}

/** Convert a camera's spherical direction to latitude/longitude in degrees */
export function cameraLatLonDeg(theta: number, phi: number): { lat: number; lon: number } {
	const geo = new geographic(new spherical(1, theta, phi).cartesian)
	return { lat: geo.latitude * trig.deg, lon: geo.longitude * trig.deg }
}

/** Compact relative-time label for view lists ("just now", "3h ago") */
export function relativeTime(iso: string): string {
	const seconds = (Date.now() - Date.parse(iso)) / 1000
	if (seconds < 60) return "just now"
	if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`
	if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`
	return `${Math.floor(seconds / 86400)}d ago`
}

/** Great-circle distance between two lat/lon points in km */
export function haversineKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
	const rad = Math.PI / 180
	const dLat = (lat2 - lat1) * rad
	const dLon = (lon2 - lon1) * rad
	const a = Math.sin(dLat / 2) ** 2 +
		Math.cos(lat1 * rad) * Math.cos(lat2 * rad) * Math.sin(dLon / 2) ** 2
	return 12742 * Math.asin(Math.min(1, Math.sqrt(a)))
}
